import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { DoorOpen, Sparkles, Trash2, Lock, LockOpen, MapPin, Puzzle, Eye, EyeOff, Upload } from "lucide-react";
import { EffectEditor, EMPTY_EFFECT, cleanEffect } from "./ObstacleCreateDialog";
import { SKILLS } from "./ObstacleCreateDialog";

export default function DoorCreateDialog({ open, onOpenChange, gameId, game, sceneId, x, y, existing, resetKey, onSaved, onDeleted, onPickArrivalOnMap, pickedArrivalX, pickedArrivalY, onArrivalConsumed, isPortal }) {
  const [name, setName] = useState(isPortal ? "Portal" : "Door");
  const [destinationType, setDestinationType] = useState("map");
  const [targetSceneId, setTargetSceneId] = useState("");
  const [targetShowId, setTargetShowId] = useState("");
  const [myGameShows, setMyGameShows] = useState([]);
  const [targetX, setTargetX] = useState("");
  const [targetY, setTargetY] = useState("");
  const [locked, setLocked] = useState(false);
  const [pickDc, setPickDc] = useState(15);
  const [breakDc, setBreakDc] = useState("");
  const [pickFailEffect, setPickFailEffect] = useState(EMPTY_EFFECT);
  const [breakFailEffect, setBreakFailEffect] = useState(EMPTY_EFFECT);
  const [conditions, setConditions] = useState([]);
  const [autoReturn, setAutoReturn] = useState(true);
  const [puzzleType, setPuzzleType] = useState("none");
  const [puzzleItems, setPuzzleItems] = useState("");
  const [puzzleQuestion, setPuzzleQuestion] = useState("");
  const [puzzleAnswer, setPuzzleAnswer] = useState("");
  const [isVisible, setIsVisible] = useState(!isPortal);
  const [teleportScope, setTeleportScope] = useState("single");
  const [triggerMode, setTriggerMode] = useState(isPortal ? "notify_gm" : "manual");
  const [triggerDc, setTriggerDc] = useState("");
  const [triggerSkill, setTriggerSkill] = useState("");
  const [triggerFailEffect, setTriggerFailEffect] = useState(EMPTY_EFFECT);
  const [playTransition, setPlayTransition] = useState(!!isPortal);
  const [transitionMediaUrl, setTransitionMediaUrl] = useState("");
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const otherScenes = (game?.scenes || []).filter((s) => s.id !== sceneId);

  useEffect(() => {
    api.get("/pf2e/conditions").then((r) => setConditions(r.data)).catch(() => setConditions([]));
    api.get("/game-shows/mine").then((r) => setMyGameShows((r.data || []).filter((s) => s.game_id === gameId && s.my_role === "host"))).catch(() => setMyGameShows([]));
  }, []);

  useEffect(() => {
    if (existing) {
      setName(existing.name || "Door");
      setDestinationType(existing.target_show_id ? "gameshow" : "map");
      setTargetSceneId(existing.target_scene_id || "");
      setTargetShowId(existing.target_show_id || "");
      setTargetX(existing.target_x != null ? String(existing.target_x) : "");
      setTargetY(existing.target_y != null ? String(existing.target_y) : "");
      setLocked(!!existing.locked);
      setPickDc(existing.pick_dc ?? 15);
      setBreakDc(existing.break_dc != null ? String(existing.break_dc) : "");
      setPickFailEffect({ ...EMPTY_EFFECT, ...(existing.pick_fail_effect || {}), message: existing.pick_fail_effect?.message || "", damage_dice: existing.pick_fail_effect?.damage_dice || "", damage_type: existing.pick_fail_effect?.damage_type || "" });
      setBreakFailEffect({ ...EMPTY_EFFECT, ...(existing.break_fail_effect || {}), message: existing.break_fail_effect?.message || "", damage_dice: existing.break_fail_effect?.damage_dice || "", damage_type: existing.break_fail_effect?.damage_type || "" });
      setPuzzleType(existing.puzzle_lock?.type || "none");
      setPuzzleItems((existing.puzzle_lock?.required_item_names || []).join(", "));
      setPuzzleQuestion(existing.puzzle_lock?.riddle_question || "");
      setPuzzleAnswer(existing.puzzle_lock?.riddle_answer || "");
      setIsVisible(existing.is_visible !== false);
      setTeleportScope(existing.teleport_scope || "single");
      setTriggerMode(existing.trigger_mode || "manual");
      setTriggerDc(existing.trigger_dc != null ? String(existing.trigger_dc) : "");
      setTriggerSkill(existing.trigger_skill || "");
      setTriggerFailEffect({ ...EMPTY_EFFECT, ...(existing.trigger_fail_effect || {}), message: existing.trigger_fail_effect?.message || "", damage_dice: existing.trigger_fail_effect?.damage_dice || "", damage_type: existing.trigger_fail_effect?.damage_type || "" });
      setPlayTransition(!!existing.play_transition);
      setTransitionMediaUrl(existing.transition_media_url || "");
    } else {
      setName(isPortal ? "Portal" : "Door");
      setDestinationType("map");
      setTargetSceneId(otherScenes[0]?.id || "");
      setTargetShowId(myGameShows[0]?.id || "");
      setTargetX("");
      setTargetY("");
      setLocked(false);
      setPickDc(15);
      setBreakDc("");
      setPickFailEffect(EMPTY_EFFECT);
      setBreakFailEffect(EMPTY_EFFECT);
      setPuzzleType("none");
      setPuzzleItems("");
      setPuzzleQuestion("");
      setPuzzleAnswer("");
      setIsVisible(!isPortal);
      setTeleportScope("single");
      setTriggerMode(isPortal ? "notify_gm" : "manual");
      setTriggerDc("");
      setTriggerSkill("");
      setTriggerFailEffect(EMPTY_EFFECT);
      setPlayTransition(!!isPortal);
      setTransitionMediaUrl("");
    }
    // Deliberately keyed on `resetKey` (identity of the door being created/edited), NOT `open` —
    // the dialog is toggled closed/reopened while the GM is out picking an arrival point on the
    // destination map, and re-running this reset on every open/close would wipe out everything
    // they'd already typed in (name, locked, DC...) before starting the pick.
  }, [existing, resetKey]);

  // Consume a point clicked on the destination map (see MapCanvas's arrival-pick flow).
  useEffect(() => {
    if (pickedArrivalX != null && pickedArrivalY != null) {
      setTargetX(String(Math.round(pickedArrivalX)));
      setTargetY(String(Math.round(pickedArrivalY)));
      onArrivalConsumed?.();
    }
  }, [pickedArrivalX, pickedArrivalY]);

  const save = async () => {
    if (destinationType === "map" && !targetSceneId) return toast.error("Pick a destination map first");
    if (destinationType === "gameshow" && !targetShowId) return toast.error("Pick a Game Show first");
    const puzzleLock = puzzleType === "none" ? null : puzzleType === "riddle"
      ? { type: "riddle", riddle_question: puzzleQuestion.trim(), riddle_answer: puzzleAnswer.trim(), solved: existing?.puzzle_lock?.solved || false }
      : { type: "items", required_item_names: puzzleItems.split(",").map((s) => s.trim()).filter(Boolean), solved: existing?.puzzle_lock?.solved || false };
    const payload = {
      name: name.trim() || (isPortal ? "Portal" : "Door"),
      target_scene_id: destinationType === "map" ? targetSceneId : null,
      target_show_id: destinationType === "gameshow" ? targetShowId : null,
      target_x: destinationType === "map" && targetX.trim() ? parseFloat(targetX) : null,
      target_y: destinationType === "map" && targetY.trim() ? parseFloat(targetY) : null,
      locked,
      pick_dc: parseInt(pickDc, 10) || 15,
      break_dc: breakDc.trim() ? parseInt(breakDc, 10) : null,
      pick_fail_effect: cleanEffect(pickFailEffect),
      break_fail_effect: cleanEffect(breakFailEffect),
      puzzle_lock: puzzleLock,
      is_visible: isVisible,
      teleport_scope: teleportScope,
      trigger_mode: triggerMode,
      trigger_dc: triggerMode === "auto" && triggerDc.trim() ? parseInt(triggerDc, 10) : null,
      trigger_skill: triggerMode === "auto" ? (triggerSkill || null) : null,
      trigger_fail_effect: cleanEffect(triggerFailEffect),
      play_transition: playTransition,
      transition_media_url: playTransition ? (transitionMediaUrl || null) : null,
    };
    try {
      if (existing) {
        const { data } = await api.patch(`/games/${gameId}/doors/${existing.id}`, payload);
        onSaved?.(data);
      } else {
        const { data } = await api.post(`/games/${gameId}/doors`, { scene_id: sceneId, x, y, auto_return: destinationType === "map" ? autoReturn : false, ...payload });
        onSaved?.(data);
        if (data.return_door) toast.success(`Return door "${data.return_door.name}" auto-created on ${targetScene?.name || "the destination map"}`);
      }
      onOpenChange(false);
    } catch {
      toast.error("Could not save the door");
    }
  };

  const remove = async () => {
    if (!existing) return;
    if (!window.confirm(`Delete "${existing.name || "this door"}"? This can't be undone.`)) return;
    await api.delete(`/games/${gameId}/doors/${existing.id}`);
    onDeleted?.(existing.id);
    onOpenChange(false);
  };

  const uploadTransitionMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMedia(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post(`/uploads?game_id=${gameId}&kind=portal_transition`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setTransitionMediaUrl(data.url);
      toast.success("Transition media uploaded");
    } catch {
      toast.error("Could not upload that file");
    } finally {
      setUploadingMedia(false);
    }
  };

  const targetScene = otherScenes.find((s) => s.id === targetSceneId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="door-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            {isPortal ? <Sparkles className="w-5 h-5 text-[#D4AF37]" /> : <DoorOpen className="w-5 h-5 text-[#D4AF37]" />} {existing ? `Edit ${isPortal ? "Portal" : "Door"}` : `New ${isPortal ? "Portal" : "Door"}`}
          </DialogTitle>
          <DialogDescription>{isPortal ? "A teleport point — visible or hidden, manual or automatic, your call." : "Clicking this on the map sends a player (or the party) to another map."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs text-[#f1e0c5]/50">Name</label>
            <Input data-testid="door-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cellar Door" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-[#f1e0c5]/50">Leads To</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="door-destination-type-map"
                onClick={() => setDestinationType("map")}
                className={`rounded-full py-2 text-xs font-semibold border ${destinationType === "map" ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "border-white/15 text-[#f1e0c5]/60"}`}
              >
                Another Map
              </button>
              <button
                type="button"
                data-testid="door-destination-type-gameshow"
                onClick={() => setDestinationType("gameshow")}
                disabled={myGameShows.length === 0}
                className={`rounded-full py-2 text-xs font-semibold border disabled:opacity-40 ${destinationType === "gameshow" ? "bg-[#D90429] border-[#D90429] text-white" : "border-white/15 text-[#f1e0c5]/60"}`}
              >
                A Game Show
              </button>
            </div>
            {myGameShows.length === 0 && <p className="text-[10px] text-[#f1e0c5]/30">Host a Game Show for this table first to link a door to it.</p>}
          </div>
          {destinationType === "gameshow" ? (
            <div className="space-y-1.5">
              <label className="text-xs text-[#f1e0c5]/50">Which Game Show</label>
              <Select value={targetShowId} onValueChange={setTargetShowId}>
                <SelectTrigger data-testid="door-target-show-select"><SelectValue placeholder="Choose a Game Show…" /></SelectTrigger>
                <SelectContent>{myGameShows.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[10px] text-[#f1e0c5]/30">Whoever steps through gets pulled into the Game Show room instead of moving to another map — no arrival point needed.</p>
            </div>
          ) : (
          <div className="space-y-1.5">
            <label className="text-xs text-[#f1e0c5]/50">Destination Map</label>
            {otherScenes.length === 0 ? (
              <p className="text-xs text-[#f1e0c5]/40">Create another map first (the "+" tab at the top-left) before placing a door.</p>
            ) : (
              <Select value={targetSceneId} onValueChange={setTargetSceneId}>
                <SelectTrigger data-testid="door-target-scene-select"><SelectValue placeholder="Choose a map…" /></SelectTrigger>
                <SelectContent>{otherScenes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          )}
          {destinationType === "map" && (
          <div className="space-y-1.5">
            <label className="text-xs text-[#f1e0c5]/50">Arrival Point on {targetScene?.name || "destination map"}</label>
            {targetX.trim() && targetY.trim() ? (
              <div className="flex items-center justify-between gap-2 bg-[#4d7c0f]/15 border border-[#4d7c0f]/40 rounded-lg px-3 py-2">
                <span className="text-xs text-[#f1e0c5]/70">Arrival point set ✓</span>
                <div className="flex gap-2">
                  <Button type="button" data-testid="door-repick-arrival-button" size="sm" variant="secondary" className="h-7 rounded-full text-xs" onClick={() => onPickArrivalOnMap?.(targetSceneId)} disabled={!targetSceneId}>Re-Pick</Button>
                  <Button type="button" data-testid="door-clear-arrival-button" size="sm" variant="ghost" className="h-7 rounded-full text-xs" onClick={() => { setTargetX(""); setTargetY(""); }}>Clear</Button>
                </div>
              </div>
            ) : (
              <Button type="button" data-testid="door-pick-arrival-button" onClick={() => onPickArrivalOnMap?.(targetSceneId)} disabled={!targetSceneId} variant="secondary" className="w-full rounded-full gap-2">
                <MapPin className="w-3.5 h-3.5" /> Click to Place Arrival Point
              </Button>
            )}
            <p className="text-[10px] text-[#f1e0c5]/30">Opens the destination map so you can click exactly where players should land. Leave unset to drop arrivals at the map's center.</p>
          </div>
          )}
          {!existing && destinationType === "map" && (
            <label className="flex items-start gap-2.5 bg-black/20 border border-white/10 rounded-lg px-3 py-2.5 cursor-pointer">
              <input
                type="checkbox"
                data-testid="door-auto-return-checkbox"
                checked={autoReturn}
                onChange={(e) => setAutoReturn(e.target.checked)}
                className="mt-0.5 accent-[#1E3A8A]"
              />
              <span>
                <span className="text-xs text-[#f1e0c5]/70 block">Auto-create a matching return door</span>
                <span className="text-[10px] text-[#f1e0c5]/30 block">Places a door on the destination map, right at the arrival point above, leading straight back here. Uncheck for a deliberate one-way door (trapdoor, chute...). Requires an arrival point to be set.</span>
              </span>
            </label>
          )}

          <div className="border-t border-white/10 pt-3 space-y-2.5">
            <button
              type="button"
              data-testid="door-locked-toggle"
              onClick={() => setLocked((v) => !v)}
              className={`w-full flex items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold border ${locked ? "bg-[#D90429] border-[#D90429] text-white" : "border-white/15 text-[#f1e0c5]/60"}`}
            >
              {locked ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />} {locked ? "Locked" : "Unlocked"}
            </button>
            {locked && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <label className="text-xs text-[#f1e0c5]/50">Pick Lock DC</label>
                    <Input data-testid="door-pick-dc-input" type="number" value={pickDc} onChange={(e) => setPickDc(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-[#f1e0c5]/50">Break Down DC (blank = not allowed)</label>
                    <Input data-testid="door-break-dc-input" type="number" value={breakDc} onChange={(e) => setBreakDc(e.target.value)} placeholder="e.g. 18" />
                  </div>
                </div>
                <EffectEditor label="On Failed Pick Attempt" effect={pickFailEffect} onChange={setPickFailEffect} conditions={conditions} testPrefix="door-pick-fail-effect" />
                {breakDc.trim() && (
                  <EffectEditor label="On Failed Break Down" effect={breakFailEffect} onChange={setBreakFailEffect} conditions={conditions} testPrefix="door-break-fail-effect" />
                )}
              </>
            )}
          </div>

          <div className="border-t border-white/10 pt-3 space-y-2.5">
            <p className="text-xs text-[#f1e0c5]/50 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" /> Portal Behavior</p>

            <button
              type="button"
              data-testid="door-visible-toggle"
              onClick={() => setIsVisible((v) => !v)}
              className={`w-full flex items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold border ${isVisible ? "border-white/15 text-[#f1e0c5]/60" : "bg-[#a855f7]/20 border-[#a855f7]/60 text-[#a855f7]"}`}
            >
              {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />} {isVisible ? "Visible to Players" : "Hidden — GM Only"}
            </button>

            <div className="space-y-1.5">
              <label className="text-xs text-[#f1e0c5]/50">Takes</label>
              <Select value={teleportScope} onValueChange={setTeleportScope}>
                <SelectTrigger data-testid="door-teleport-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Just the player who steps on it</SelectItem>
                  <SelectItem value="same_map">Every player on this same map</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-[#f1e0c5]/50">Trigger</label>
              <Select value={triggerMode} onValueChange={setTriggerMode}>
                <SelectTrigger data-testid="door-trigger-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual — a player has to click it</SelectItem>
                  <SelectItem value="auto">Auto-teleport on step</SelectItem>
                  <SelectItem value="notify_gm">Notify me only — I decide when it fires</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {triggerMode === "auto" && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <label className="text-xs text-[#f1e0c5]/50">Roll DC (blank = instant, no roll)</label>
                    <Input data-testid="door-trigger-dc-input" type="number" value={triggerDc} onChange={(e) => setTriggerDc(e.target.value)} placeholder="e.g. 15" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-[#f1e0c5]/50">Skill</label>
                    <Select value={triggerSkill} onValueChange={setTriggerSkill} disabled={!triggerDc.trim()}>
                      <SelectTrigger data-testid="door-trigger-skill-select"><SelectValue placeholder="Any/Flat check" /></SelectTrigger>
                      <SelectContent>{SKILLS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {triggerDc.trim() && (
                  <EffectEditor label="On Failed Roll (portal doesn't fire)" effect={triggerFailEffect} onChange={setTriggerFailEffect} conditions={conditions} testPrefix="door-trigger-fail-effect" />
                )}
              </>
            )}

            <label className="flex items-start gap-2.5 bg-black/20 border border-white/10 rounded-lg px-3 py-2.5 cursor-pointer">
              <input type="checkbox" data-testid="door-play-transition-checkbox" checked={playTransition} onChange={(e) => setPlayTransition(e.target.checked)} className="mt-0.5 accent-[#a855f7]" />
              <span>
                <span className="text-xs text-[#f1e0c5]/70 block">Play a travel effect</span>
                <span className="text-[10px] text-[#f1e0c5]/30 block">A brief full-screen effect plays for whoever actually goes through — nobody else sees it. Built-in swirl by default, or upload your own video/GIF below.</span>
              </span>
            </label>
            {playTransition && (
              <div className="space-y-1.5">
                <label className="text-xs text-[#f1e0c5]/50">Custom Transition Video/GIF (optional)</label>
                {transitionMediaUrl ? (
                  <div className="flex items-center justify-between gap-2 bg-[#a855f7]/15 border border-[#a855f7]/40 rounded-lg px-3 py-2">
                    <span className="text-xs text-[#f1e0c5]/70">Custom media set ✓</span>
                    <Button type="button" data-testid="door-clear-transition-media-button" size="sm" variant="ghost" className="h-7 rounded-full text-xs" onClick={() => setTransitionMediaUrl("")}>Clear</Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 border border-dashed border-white/20 rounded-lg py-3 cursor-pointer text-xs text-[#f1e0c5]/50 hover:border-[#a855f7]/50">
                    <Upload className="w-3.5 h-3.5" /> {uploadingMedia ? "Uploading…" : "Upload video or GIF"}
                    <input type="file" data-testid="door-transition-media-upload" accept="video/*,image/gif" className="hidden" onChange={uploadTransitionMedia} disabled={uploadingMedia} />
                  </label>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-3 space-y-2.5">
            <p className="text-xs text-[#f1e0c5]/50 flex items-center gap-1.5"><Puzzle className="w-3.5 h-3.5 text-[#D4AF37]" /> Puzzle Lock (independent of the lock above)</p>
            <Select value={puzzleType} onValueChange={setPuzzleType}>
              <SelectTrigger data-testid="door-puzzle-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="items">Requires Items (from inventory)</SelectItem>
                <SelectItem value="riddle">Riddle Answer</SelectItem>
              </SelectContent>
            </Select>
            {puzzleType === "items" && (
              <div className="space-y-1.5">
                <label className="text-xs text-[#f1e0c5]/50">Required item names (comma-separated)</label>
                <Input data-testid="door-puzzle-items-input" value={puzzleItems} onChange={(e) => setPuzzleItems(e.target.value)} placeholder="e.g. Golden Idol, Silver Key" />
              </div>
            )}
            {puzzleType === "riddle" && (
              <>
                <div className="space-y-1.5">
                  <label className="text-xs text-[#f1e0c5]/50">Riddle (shown to players)</label>
                  <Input data-testid="door-puzzle-question-input" value={puzzleQuestion} onChange={(e) => setPuzzleQuestion(e.target.value)} placeholder="What has keys but no locks?" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[#f1e0c5]/50">Answer</label>
                  <Input data-testid="door-puzzle-answer-input" value={puzzleAnswer} onChange={(e) => setPuzzleAnswer(e.target.value)} placeholder="A piano" />
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button data-testid="door-save-button" onClick={save} className="flex-1 rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab]">Save Door</Button>
            {existing && (
              <Button data-testid="door-delete-button" onClick={remove} variant="destructive" className="rounded-full gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
